"use client"
import { useState, useEffect, useRef } from "react"
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
  useColorModeValue,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Badge,
  Spinner,
  HStack,
  Image,
  FormControl,
  FormLabel,
} from "@chakra-ui/react"
import {
  DocumentTextIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  CheckCircleIcon,
  StarIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline"
import { jsPDF } from "jspdf"
import html2canvas from "html2canvas"
import ProfessionalTemplate from "./templates/ProfessionalTemplate"
import MinimalTemplate from "./templates/MinimalTemplate"
import ModernTemplate from "./templates/ModernTemplate"
import CreativeTemplate from "./templates/CreativeTemplate"
import SimpleTemplate from "./templates/SimpleTemplate"
import ExecutiveTemplate from "./templates/ExecutiveTemplate"
import AchieverTemplate from "./templates/AchieverTemplate"
import ImpactTemplate from "./templates/ImpactTemplate"
import { dummyData } from "./dummyData"

import { useDispatch, useSelector } from "react-redux"

// Keep Dashboard slice for local data
import { fetchBasicDetailsAsync, selectBasicDetails } from "../../profile/BasicDetailsController/BasicDetailsSlice"

// IMPORTANT: Use the SAME thunks as CandidateProfile for resume upload/refresh
import {

  addCandidateResumeAsync as addCandidateResumeSingleParamAsync,
} from "../../profile/BasicDetailsSingleParam/BasicDetailsSingleParamSlice"
import { fetchBasicDetailsBySingleParamAsync, addCandidateResumeAsync } from "../../profile/BasicDetailsSingleParam/BasicDetailsSingleParamSlice"

import professional from "./images/professionaltemplate.jpeg"
import creative from "./images/creative.webp"
import minimal from "./images/minimal.jpeg"
import modern from "./images/modern.png"
import simple from "./images/simple.png"
import Executive from "./images/Executive.png"
import Achiever from "./images/Achiever.jpg"
import Impact from "./images/Impact.jpg"

import {
  fetchCandidateActiveSubscriptionsAsync,
  selectCandidateActiveSubscriptions,
} from "../../candidateDashboard/candidateSlice/CandidateActiveSubscription/CandidateActiveSubscriptionSlice"

import { Link } from "react-router-dom"
import { selectAdminResumeBuilder } from "../../AdminPanel/AdminResumeBuilder/AdminResumeBuilderSlice"
import {
  fetchCandidateActiveResumeBuilderAsync,
  createCandidateResumeBuilderAsync,
  selectActiveResumebuilder,
  selectCreateResumeBuilderState,
  clearCreateState,
} from "../../candidateDashboard/candidateSlice/CandidateActiveResumeBuilder/CandidateActiveResumeBuilderSlice"
import {
  fetchCandidateTotalResumesAsync,
  selectCandidateTotalResumes,
} from "../../candidateDashboard/candidateSlice/CandidateActiveResumeBuilder/CandidateResumeBuilderSlice"

// Template preview images
const templateImages = {
  professional: professional,
  creative: creative,
  minimal: minimal,
  modern: modern,
  simple: simple,
  Executive: Executive,
  Achiever: Achiever,
  Impact: Impact,
}

const ResumeDashboard = () => {
  const [searchQuery, setSearchQuery] = useState("")
  const [candidateData, setCandidateData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [isPreviewMode, setIsPreviewMode] = useState(true) // true = dummy data, false = real data

  // Upload modal states
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [selectedResumeFile, setSelectedResumeFile] = useState(null)
  const [previewResumeUrl, setPreviewResumeUrl] = useState("")
  const [isUploading, setIsUploading] = useState(false)

  const resumeRef = useRef()
  const toast = useToast()
  const dispatch = useDispatch()

  const basicDetails = useSelector(selectBasicDetails)
  useSelector(selectAdminResumeBuilder) // not used, but keeping in case you need it
  const activeResumeBuilderData = useSelector(selectActiveResumebuilder)
  const createResumeBuilderState = useSelector(selectCreateResumeBuilderState)
  const candidatetotalresumes = useSelector(selectCandidateTotalResumes)
  const activesubscriptions = useSelector(selectCandidateActiveSubscriptions)

  const hasResumeBuilderAccess = activesubscriptions?.data?.response?.remaining_resume_builder_apply > 0

  // Upload modal handlers
  const onUploadOpen = () => setIsUploadOpen(true)
  const onUploadClose = () => {
    setIsUploadOpen(false)
    setSelectedResumeFile(null)
    setPreviewResumeUrl("")
  }

  // Template mapping based on resume_identity
  const getTemplateByResumeIdentity = (resumeIdentity) => {
    const mapping = {
      SoftwareDeveloper: "professional",
      Creative: "creative",
      Minimal: "minimal",
      Modern: "modern",
      Simple: "simple",
      Executive: "Executive",
      Achiever: "Achiever",
      Impact: "Impact",
    }
    return mapping[resumeIdentity] || "professional"
  }

  // Get template data from API based on resume_identity
  const getTemplateDataFromAPI = (templateKey) => {
    if (!activeResumeBuilderData?.data) return null
    const identityMapping = {
      professional: "SoftwareDeveloper",
      creative: "Creative",
      minimal: "Minimal",
      modern: "Modern",
      simple: "Simple",
      Executive: "Executive",
      Achiever: "Achiever",
      Impact: "Impact",
    }
    const resumeIdentity = identityMapping[templateKey]
    return activeResumeBuilderData.data.find((item) => item.resume_identity === resumeIdentity)
  }

  // Transform API data to match existing resume card structure
  const transformApiDataToResumes = (apiData) => {
    if (!apiData?.data?.response?.data) return []
    return apiData.data.response.data.map((item) => ({
      id: item.uuid,
      title: `${item.resume_builders.title} Resume`,
      lastUpdated: new Date(item.created_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      template: getTemplateByResumeIdentity(item.resume_builders.resume_identity),
      uuid: item.uuid,
      resume_builder_id: item.resume_builder_id,
      resume_builders: item.resume_builders,
    }))
  }

  const existingResumes = transformApiDataToResumes(candidatetotalresumes)

  // Fetch candidate data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        await new Promise((resolve) => setTimeout(resolve, 1000))
        dispatch(fetchBasicDetailsAsync())
        dispatch(fetchCandidateActiveSubscriptionsAsync())
        dispatch(fetchCandidateActiveResumeBuilderAsync())
        dispatch(fetchCandidateTotalResumesAsync())
      } catch (error) {
        console.error("Error fetching data:", error)
        toast({
          title: "Error fetching data",
          description: "Unable to load your profile data",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        })
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [toast, dispatch])

  // Handle create resume builder success
  useEffect(() => {
    if (createResumeBuilderState.success) {
      toast({
        title: "Template Selected Successfully",
        description: "You can now use this template to create your resume",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
      dispatch(clearCreateState())
      dispatch(fetchCandidateActiveResumeBuilderAsync())
      dispatch(fetchCandidateTotalResumesAsync())
    }
  }, [createResumeBuilderState.success, dispatch, toast])

  // Handle create resume builder error
  useEffect(() => {
    if (createResumeBuilderState.error) {
      toast({
        title: "Error Selecting Template",
        description: createResumeBuilderState.error,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      })
      dispatch(clearCreateState())
    }
  }, [createResumeBuilderState.error, dispatch, toast])

  const resumeTemplates = [
    {
      id: 1,
      name: "Professional",
      category: "Corporate",
      popularity: "High",
      color: "blue.500",
      image: templateImages.professional,
      component: ProfessionalTemplate,
      key: "professional",
    },
    {
      id: 2,
      name: "Creative",
      category: "Design",
      popularity: "Medium",
      color: "purple.500",
      image: templateImages.creative,
      component: CreativeTemplate,
      key: "creative",
    },
    {
      id: 3,
      name: "Minimal",
      category: "All-purpose",
      popularity: "High",
      color: "gray.500",
      image: templateImages.minimal,
      component: MinimalTemplate,
      key: "minimal",
    },
    {
      id: 4,
      name: "Modern",
      category: "Tech",
      popularity: "Medium",
      color: "green.500",
      image: templateImages.modern,
      component: ModernTemplate,
      key: "modern",
    },
    {
      id: 5,
      name: "Simple",
      category: "Entry-level",
      popularity: "High",
      color: "red.500",
      image: templateImages.simple,
      component: SimpleTemplate,
      key: "simple",
    },
    {
      id: 6,
      name: "Executive",
      category: "Entry-level",
      popularity: "High",
      color: "red.500",
      image: templateImages.Executive,
      component: ExecutiveTemplate,
      key: "Executive",
    },
    {
      id: 7,
      name: "Achiever",
      category: "Design",
      popularity: "High",
      color: "red.500",
      image: templateImages.Achiever,
      component: AchieverTemplate,
      key: "Achiever",
    },
    {
      id: 8,
      name: "Impact",
      category: "Corporate",
      popularity: "High",
      color: "red.500",
      image: templateImages.Impact,
      component: ImpactTemplate,
      key: "Impact", // FIXED (was "ImpactTemplate")
    },
  ]

  const filteredResumes = existingResumes.filter((resume) =>
    resume.title.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  const cardBgColor = useColorModeValue("white", "gray.700")
  const textColor = useColorModeValue("gray.800", "white")
  const mutedColor = useColorModeValue("gray.600", "gray.400")

  const totalApplications = existingResumes.reduce((sum, resume) => sum + (resume.applications || 0), 0)

  // ============ MODIFIED CODE START ============
  // File selection for PDF, DOC, and DOCX
  const handleResumeFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword", // for .doc
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // for .docx
      ]

      if (allowedTypes.includes(file.type)) {
        setSelectedResumeFile(file)
        setPreviewResumeUrl(URL.createObjectURL(file))
      } else {
        toast({
          title: "Invalid File Type",
          description: "Please upload a valid PDF, DOC, or DOCX file.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        })
        // Reset the input and state if an invalid file is selected
        e.target.value = null
        setSelectedResumeFile(null)
        setPreviewResumeUrl("")
      }
    }
  }
  // ============ MODIFIED CODE END ============

  // Save uploaded resume using the SAME thunk as CandidateProfile
  const saveResume = async () => {
    if (!selectedResumeFile) {
      toast({
        title: "Resume Upload Error",
        description: "The resume field is required.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      })
      return
    }

    const formData = new FormData()
    formData.append("resume", selectedResumeFile)

    try {
      setIsUploading(true)
      const response = await dispatch(addCandidateResumeSingleParamAsync({ formData }))

      // Refresh BOTH slices to keep the entire application in sync.
      await Promise.all([
        dispatch(fetchBasicDetailsAsync()), // This updates the Dashboard's data
        dispatch(fetchBasicDetailsBySingleParamAsync()), // This updates the Profile's data
      ])

      onUploadClose()
      if (response?.payload?.status === "success") {
        toast({
          title: "Resume Upload Successful",
          description: "Your resume has been uploaded successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        })
      } else {
        toast({
          title: "Resume Upload Failed",
          description: "Unable to upload resume. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        })
      }
    } catch (error) {
      toast({
        title: "Resume Upload Error",
        description: "An unexpected error occurred while uploading resume.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      })
    } finally {
      setIsUploading(false)
    }
  }

  // Generate PDF and open upload modal
  const generatePDFAndOpenUpload = async () => {
    if (!hasResumeBuilderAccess) {
      toast({
        title: "Subscription Required",
        description: "You need to upgrade your subscription to download resumes",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      })
      return
    }

    if (!resumeRef.current) return
    setIsPrinting(true)
    try {
      const resumeElement = resumeRef.current

      const pdfContainer = document.createElement("div")
      pdfContainer.style.position = "absolute"
      pdfContainer.style.left = "-9999px"
      pdfContainer.style.width = "210mm"
      pdfContainer.style.minHeight = "297mm"
      pdfContainer.style.backgroundColor = "white"
      pdfContainer.style.padding = "2mm"
      pdfContainer.style.boxSizing = "border-box"
      pdfContainer.style.boxShadow = "0 0 5px rgba(0,0,0,0.1)"
      document.body.appendChild(pdfContainer)

      const clone = resumeElement.cloneNode(true)
      clone.style.width = "100%"
      clone.style.margin = "0"
      clone.style.padding = "0"
      clone.style.transform = "none"
      clone.style.overflow = "visible"
      clone.style.fontSize = "12pt"
      clone.style.lineHeight = "1.5"
      clone.style.height = "auto"
      clone.style.maxHeight = "none"
      pdfContainer.appendChild(clone)

      await new Promise((resolve) => {
        const checkReady = () => {
          const images = Array.from(clone.querySelectorAll("img"))
          const fontsReady = document.fonts ? document.fonts.ready : Promise.resolve()
          Promise.all([
            Promise.all(
              images.map((img) =>
                img.complete
                  ? Promise.resolve()
                  : new Promise((res) => {
                    img.onload = res
                    img.onerror = res
                  }),
              ),
            ),
            fontsReady,
          ])
            .then(resolve)
            .catch(resolve)
        }
        checkReady()
        setTimeout(resolve, 2000)
      })

      const canvas = await html2canvas(pdfContainer, {
        scale: 4,
        logging: false,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: 0,
        windowWidth: pdfContainer.scrollWidth,
        windowHeight: pdfContainer.scrollHeight,
        letterRendering: true,
        onclone: (clonedDoc) => {
          const elements = clonedDoc.querySelectorAll("*")
          elements.forEach((el) => {
            el.style.visibility = "visible"
            el.style.opacity = "1"
            el.style.boxShadow = "none"
            el.style.textShadow = "none"
          })
          const interactive = clonedDoc.querySelectorAll("button, a, [onclick]")
          interactive.forEach((el) => (el.style.display = "none"))
        },
      })
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error("Canvas rendering failed - no content")
      }

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        hotfixes: ["px_scaling"],
        putOnlyUsedFonts: true,
      })

      const pageWidth = 210 - 30
      const pageHeight = 297 - 30
      const imgRatio = canvas.width / canvas.height
      let imgWidth = pageWidth
      let imgHeight = pageWidth / imgRatio
      if (imgHeight > pageHeight) {
        imgHeight = pageHeight
        imgWidth = pageHeight * imgRatio
      }
      const xPos = (210 - imgWidth) / 2
      const yPos = (297 - imgHeight) / 2

      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", xPos, yPos, imgWidth, imgHeight, undefined, "FAST")

      document.body.removeChild(pdfContainer)

      const pdfName = isPreviewMode
        ? "Sample_Resume.pdf"
        : `${(dataToUse?.name || "Candidate").replace(/[^a-z0-9]/gi, "_")}_Resume.pdf`

      pdf.save(pdfName)
      toast({
        title: "Resume downloaded",
        description: "Your resume has been downloaded as a high-quality PDF",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
      // Open the upload modal after successful download
      onUploadOpen()

    } catch (error) {
      console.error("Error generating PDF:", error)
      toast({
        title: "Error generating PDF",
        description: error.message || "There was an error creating your PDF. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setIsPrinting(false)
    }
  }

  const handleTemplatePreview = (template) => {
    setSelectedTemplate(template)
    setIsPreviewMode(true)
    setIsPreviewOpen(true)
  }

  const handleTemplateSelect = async (templateKey) => {
    if (!hasResumeBuilderAccess) {
      toast({
        title: "Subscription Required",
        description: "You need to upgrade your subscription to use this template",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      })
      return
    }
    const templateData = getTemplateDataFromAPI(templateKey)
    if (!templateData) {
      toast({
        title: "Template Not Found",
        description: "Unable to find template data",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      })
      return
    }
    if (templateData.resume_builder_candidate_count === 1) {
      setSelectedTemplate(templateKey)
      setIsPreviewMode(false)
      setIsPreviewOpen(true)
      return
    }
    if (templateData.resume_builder_candidate_count > 1) {
      toast({
        title: "Template Error",
        description: "There's an issue with this template. Please contact support.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      })
      return
    }
    try {
      await dispatch(createCandidateResumeBuilderAsync(templateData.uuid)).unwrap()
      setSelectedTemplate(templateKey)
      setIsPreviewMode(false)
      setIsPreviewOpen(true)
    } catch (error) {
      console.error("Error creating resume builder:", error)
    }
  }

  const handleClosePreview = () => {
    setIsPreviewOpen(false)
  }

  const SelectedTemplateComponent = selectedTemplate
    ? resumeTemplates.find((t) => t.key === selectedTemplate)?.component
    : null

  // Determine which data to use based on preview mode
  const dataToUse = isPreviewMode ? dummyData : candidateData || basicDetails

  return (
    <Box className="">
      <div style={{ fontFamily: "'Inter', sans-serif" }}>
        <div className="py-8 min-h-screen bg-mainBg sm:px-6 sm:py-4">
          <div className="px-4 sm:px-0.5">
            {/* Header */}
            <Flex
              justifyContent="space-between"
              alignItems="center"
              mb={8}
              pb={6}
              borderBottomWidth="1px"
              borderColor="gray.200"
              flexWrap={"wrap"}
            >
              <VStack align="start" spacing={1}>
                <Heading as="h1" size="xl" color={textColor} fontWeight="bold">
                  Resume Dashboard
                </Heading>
                <Text color={mutedColor}>Create and manage your professional resumes</Text>
              </VStack>
            </Flex>

            {/* Stats */}
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6} mb={10}>
              <StatCard
                icon={DocumentTextIcon}
                title="Total Resumes"
                value={candidatetotalresumes?.data?.response?.total || 0}
                description="Active resumes in your account"
                color="blue"
              />
              <StatCard
                icon={ChartBarIcon}
                title="Applications"
                value={totalApplications}
                description="Jobs applied with your resumes"
                color="green"
              />
              <StatCard
                icon={CheckCircleIcon}
                title="Interviews"
                value="0"
                description="Interviews scheduled this month"
                color="purple"
              />
            </Grid>

            {/* My Resumes */}
            <VStack spacing={6} align="stretch" mb={12}>
              <Flex justifyContent="space-between" alignItems="center" wrap={"wrap"} mb={4}>
                <Heading as="h2" size="lg" color={textColor} fontWeight="bold">
                  My Resumes ({candidatetotalresumes?.data?.response?.total || 0})
                </Heading>
                <InputGroup maxW="250px">
                  <InputLeftElement pointerEvents="none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search resumes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    borderRadius="full"
                    bg="white"
                  />
                </InputGroup>
              </Flex>
              {candidatetotalresumes?.loading ? (
                <Flex justify="center" align="center" h="200px" direction="column" gap={4}>
                  <Spinner size="xl" color="blue.500" thickness="4px" />
                  <Text>Loading your resumes...</Text>
                </Flex>
              ) : existingResumes.length > 0 ? (
                <Grid templateColumns={{ base: "1fr", md: "repeat(auto-fill, minmax(300px, 1fr))" }} gap={6}>
                  {filteredResumes.map((resume) => (
                    <ResumeCard
                      key={resume.id}
                      resume={resume}
                      onPreview={() => handleTemplatePreview(resume.template)}
                      onSelect={() => handleTemplateSelect(resume.template)}
                    />
                  ))}
                </Grid>
              ) : (
                <Box
                  bg={cardBgColor}
                  p={8}
                  borderRadius="xl"
                  textAlign="center"
                  border="2px dashed"
                  borderColor="gray.300"
                >
                  <DocumentTextIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <Heading as="h3" size="md" color={textColor} mb={2}>
                    No Resumes Yet
                  </Heading>
                  <Text color={mutedColor} mb={4}>
                    Start by selecting a template below to create your first resume
                  </Text>
                </Box>
              )}
            </VStack>

            {/* Resume Templates */}
            <VStack spacing={6} align="stretch" mb={12}>
              <Flex justifyContent="space-between" alignItems="center" mb={4} flexWrap={"wrap"}>
                <Heading as="h2" size="lg" color={textColor} fontWeight="bold">
                  Resume Templates
                </Heading>
              </Flex>
              <Grid
                templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" }}
                gap={6}
              >
                {resumeTemplates.map((template) => {
                  const templateData = getTemplateDataFromAPI(template.key)
                  const isTemplateUsed = templateData?.resume_builder_candidate_count > 0
                  return (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      onPreview={() => handleTemplatePreview(template.key)}
                      onSelect={() => handleTemplateSelect(template.key)}
                      hasResumeBuilderAccess={hasResumeBuilderAccess}
                      active={activesubscriptions}
                      isUsed={isTemplateUsed}
                      isCreating={createResumeBuilderState.loading}
                    />
                  )
                })}
              </Grid>
            </VStack>
          </div>
          <div className="text-center text-gray-600 mt-12 text-sm">
            <p>© {new Date().getFullYear()} IndiaHirings.com All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Resume Preview Modal */}
      <Modal isOpen={isPreviewOpen} onClose={handleClosePreview} isCentered size="5xl">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader bg={isPreviewMode ? "gray.100" : "blue.50"} borderTopRadius="xl" py={4}>
            <Flex justifyContent="space-between" alignItems="center">
              <Text>
                {isPreviewMode ? "Template Preview" : "Your Resume"}{" "}
                {selectedTemplate && (
                  <Badge colorScheme={isPreviewMode ? "gray" : "blue"} ml={2}>
                    {resumeTemplates.find((t) => t.key === selectedTemplate)?.name}
                  </Badge>
                )}
              </Text>
              <Badge colorScheme={isPreviewMode ? "orange" : "green"} fontSize="sm" px={3} py={1} borderRadius="full">
                {isPreviewMode ? "Sample Data" : "Your Data"}
              </Badge>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={6}>
            {loading ? (
              <Flex justify="center" align="center" h="60vh" direction="column" gap={4}>
                <Spinner size="xl" color="blue.500" thickness="4px" />
                <Text>Loading your resume data...</Text>
              </Flex>
            ) : (
              <Box
                ref={resumeRef}
                bg="white"
                p={4}
                boxShadow="md"
                borderRadius="md"
                className="print-container"
                overflow="hidden"
                maxH="70vh"
                overflowY="auto"
                sx={{
                  "@media print": {
                    padding: "0",
                    boxShadow: "none",
                    borderRadius: "0",
                    maxHeight: "none",
                    overflow: "visible",
                  },
                }}
              >
                {SelectedTemplateComponent && <SelectedTemplateComponent candidateData={dataToUse} />}
              </Box>
            )}
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="xl" py={4}>
            <HStack spacing={4}>
              {isPreviewMode ? (
                hasResumeBuilderAccess ? (
                  <Button
                    colorScheme="blue"
                    onClick={() => {
                      const templateData = getTemplateDataFromAPI(selectedTemplate)
                      if (templateData?.resume_builder_candidate_count === 1) {
                        setIsPreviewMode(false)
                        return
                      }
                      if (templateData?.resume_builder_candidate_count > 1) {
                        toast({
                          title: "Template Error",
                          description: "There's an issue with this template.",
                          status: "error",
                          duration: 3000,
                          isClosable: true,
                          position: "top-right",
                        })
                        return
                      }
                      handleTemplateSelect(selectedTemplate)
                    }}
                    leftIcon={<CheckCircleIcon className="h-5 w-5" />}
                    isLoading={createResumeBuilderState.loading}
                  >
                    Use This Template
                  </Button>
                ) : (
                  <Link to="/candidate-dashboard/subscription-plan">
                    <Button colorScheme="green">Buy Subscription</Button>
                  </Link>
                )
              ) : (
                <Button
                  colorScheme="blue"
                  onClick={generatePDFAndOpenUpload}
                  isDisabled={loading || !SelectedTemplateComponent || isPrinting}
                  leftIcon={isPrinting ? <Spinner size="sm" /> : <ArrowDownTrayIcon className="h-5 w-5" />}
                >
                  {isPrinting ? "Generating PDF..." : "Download & Upload"}
                </Button>
              )}
              <Button variant="ghost" onClick={handleClosePreview}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ============ MODIFIED MODAL START ============ */}
      <Modal isOpen={isUploadOpen} onClose={onUploadClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upload New Resume</ModalHeader>
          <ModalBody>
            <FormControl>
              <FormLabel htmlFor="resume">Select Resume (PDF, DOC, DOCX)</FormLabel>
              <Input
                type="file"
                id="resume"
                accept=".pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleResumeFileChange}
              />
              {previewResumeUrl && selectedResumeFile && (
                <Box mt={4}>
                  <Text>Selected File:</Text>
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    {selectedResumeFile.name}
                  </Text>
                  <a
                    href={previewResumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline hover:text-blue-700"
                  >
                    Preview / Download File
                  </a>
                </Box>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onUploadClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={saveResume} ml={3} isLoading={isUploading} loadingText="Uploading...">
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* ============ MODIFIED MODAL END ============ */}
    </Box>
  )
}

const StatCard = ({ icon: Icon, title, value, description, color }) => {
  const bgColor = useColorModeValue("white", "gray.700")
  const textColor = useColorModeValue("gray.800", "white")
  const mutedColor = useColorModeValue("gray.600", "gray.400")
  return (
    <Box
      bg={bgColor}
      p={6}
      borderRadius="xl"
      boxShadow="md"
      transition="transform 0.2s"
      _hover={{ transform: "translateY(-5px)" }}
    >
      <Flex alignItems="center" mb={3}>
        <Box
          bg={`${color}.50`}
          color={`${color}.500`}
          p={3}
          borderRadius="lg"
          mr={4}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon className="h-6 w-6" />
        </Box>
        <Text fontWeight="medium" color={textColor} fontSize="lg">
          {title}
        </Text>
      </Flex>
      <Text fontSize="3xl" fontWeight="bold" color={`${color}.500`} mb={2}>
        {value}
      </Text>
      <Text fontSize="sm" color={mutedColor}>
        {description}
      </Text>
    </Box>
  )
}

const ResumeCard = ({ resume, onPreview, onSelect }) => {
  const bgColor = useColorModeValue("white", "gray.700")
  const textColor = useColorModeValue("gray.800", "white")
  const mutedColor = useColorModeValue("gray.600", "gray.400")
  return (
    <Box
      bg={bgColor}
      p={6}
      borderRadius="xl"
      boxShadow="md"
      transition="transform 0.2s, box-shadow 0.2s"
      _hover={{ transform: "translateY(-5px)", boxShadow: "lg" }}
    >
      <Flex justifyContent="space-between" wrap={"wrap"} className="items-end">
        <Heading as="h4" size="md" color={textColor} fontWeight="semibold" className="truncate">
          {resume.title}
        </Heading>
      </Flex>
      <Badge colorScheme="blue" variant="subtle" fontSize="xs">
        {resume.resume_builders?.resume_identity}
      </Badge>
      <Text fontSize="sm" color={mutedColor} mb={4}>
        Created: {resume.lastUpdated}
      </Text>
      <Flex alignItems="center" mb={5}>
        <ChartBarIcon className="h-4 w-4 mr-2 text-blue-500" />
        <div fontSize="sm" color="blue.500" fontWeight="medium" className="text-blue-500 font-medium text-sm">
          {resume.applications} applications
        </div>
      </Flex>
      <div justifyContent="space-between" gap={3} className="space-y-2">
        <Button
          size="sm"
          variant="outline"
          leftIcon={<EyeIcon className="h-4 w-4 bg-transparent" />}
          onClick={onPreview}
          flex="1"
          width={"100%"}
          borderRadius="md"
        >
          Preview
        </Button>
        <Button
          size="sm"
          colorScheme="blue"
          leftIcon={<ArrowUpTrayIcon className="h-4 w-4" />}
          onClick={onSelect}
          flex="1"
          width={"100%"}
          borderRadius="md"
        >
          Upload to replace resume
        </Button>
      </div>
    </Box>
  )
}

const TemplateCard = ({ template, onPreview, onSelect, hasResumeBuilderAccess, active, isUsed, isCreating }) => {
  const bgColor = useColorModeValue("white", "gray.700")
  const textColor = useColorModeValue("gray.800", "white")
  const mutedColor = useColorModeValue("gray.600", "gray.400")
  return (
    <Box
      bg={bgColor}
      borderRadius="xl"
      overflow="hidden"
      boxShadow="md"
      transition="transform 0.2s, box-shadow 0.2s"
      _hover={{ transform: "translateY(-5px)", boxShadow: "lg" }}
    >
      <Box h="220px" bg={`linear-gradient(to right, ${template.color}, ${template.color}20)`} position="relative">
        <Image
          src={template.image || "/placeholder.svg"}
          alt={template.name}
          className={`absolute inset-0 w-full h-full object-contain p-3 ${!hasResumeBuilderAccess ? "filter blur-sm" : ""}`}
        />
        {isUsed && (
          <Box
            position="absolute"
            top="2"
            right="2"
            bg="green.500"
            color="white"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="xs"
            fontWeight="bold"
          >
            Ready
          </Box>
        )}
      </Box>
      <Box p={5}>
        <Flex justifyContent="space-between" alignItems="start" mb={2}>
          <Heading as="h3" size="md" color={textColor} fontWeight="semibold">
            {template.name}
          </Heading>
          <Flex>
            {[...Array(5)].map((_, i) => (
              <StarIcon key={i} className={`h-4 w-4 ${i < 4 ? "text-yellow-400" : "text-gray-300"}`} />
            ))}
          </Flex>
        </Flex>
        <Text fontSize="sm" color={mutedColor} mb={3}>
          {template.category}
        </Text>
        <Flex gap={2} mb={4}>
          <Badge colorScheme="blue" variant="subtle" px={2} py={1} borderRadius="full" fontSize="xs">
            {template.popularity} Popularity
          </Badge>
          {isUsed && (
            <Badge colorScheme="green" variant="subtle" px={2} py={1} borderRadius="full" fontSize="xs">
              USED
            </Badge>
          )}
        </Flex>
        <div className="space-y-2">
          <Button
            size="sm"
            variant="outline"
            leftIcon={<EyeIcon className="h-4 w-4 bg-transparent" />}
            onClick={onPreview}
            width={"100%"}
            borderRadius="md"
          >
            Preview
          </Button>
          {active?.data?.response?.remaining_resume_builder_apply > 0 ? (
            <Button
              size="sm"
              colorScheme={isUsed ? "green" : "blue"}
              leftIcon={isUsed ? <ArrowDownTrayIcon className="h-4 w-4" /> : <CheckCircleIcon className="h-4 w-4" />}
              onClick={onSelect}
              width={"100%"}
              borderRadius="md"
              isLoading={isCreating}
            >
              {isUsed ? "Download Resume" : "Use Template"}
            </Button>
          ) : (
            <Link to="/candidate-dashboard/subscription-plan">
              <Button size="sm" colorScheme="green" width={"100%"} borderRadius="md">
                Buy Subscription
              </Button>
            </Link>
          )}
        </div>
      </Box>
    </Box>
  )
}

export default ResumeDashboard